import { Box, useTheme } from '@mui/material';
import { ChevronRightSmall } from '@portals/icons';
import React from 'react';

import { FinalContainer } from '../../components/FinalGrid/FinalGrid';
import { Link } from '../../components/Link/Link';
import type { Theme } from '../../themes/themes';
import { getStyles } from './Breadcrumbs.styles';

export type Breadcrumb = {
  href?: string;
  id: string;
  name?: string;
};

export interface BreadcrumbsProps {
  /**
   * Array of Elements `{ href, id, name }`
   */
  breadcrumbs: Array<Breadcrumb>;

  /**
   * Overwrite LinkComponent if you want to change the rendered Link. It gets passed a single attribute: href and sx for additional styling information
   *
   * @default: [@portals/core/Link]
   */
  LinkComponent?: typeof Link;
}

export const Breadcrumbs: React.FunctionComponent<BreadcrumbsProps> = ({ breadcrumbs, LinkComponent = Link }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <FinalContainer disableGutters={true} sx={{ ...styles.container }} component="nav" aria-label="Breadcrumb">
      <FinalContainer sx={{ ...styles.breadcrumbsContainer }} component="ol">
        {breadcrumbs.map((breadcrumb, i) => (
          <Box key={breadcrumb.id} sx={{ ...styles.linkBox }} component="li">
            {i > 0 && <ChevronRightSmall sx={{ fontSize: { xs: '18px', lg: '24px' } }} />}
            <LinkComponent href={breadcrumb.href} sx={{ ...styles.link }}>
              {breadcrumb.name}
            </LinkComponent>
          </Box>
        ))}
      </FinalContainer>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            // eslint-disable-next-line @if6/fi-no-absolute-urls
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((breadcrumb, i) => {
              return {
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@id': breadcrumb.href,
                  name: breadcrumb.name,
                },
              };
            }),
          }),
        }}
      />
    </FinalContainer>
  );
};
